import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export const useLocationPrice = () => {
  const [searchParams] = useSearchParams();

  // ✅ URL param first, then localStorage fallback
  const locationFilter = (
    searchParams.get("loc") ||
    localStorage.getItem("selectedLocation") ||
    ""
  ).trim();

  // ✅ Helper to get active location ID from localStorage
  const getActiveLocationId = () => {
    try {
      const name = locationFilter || localStorage.getItem("selectedLocation");
      if (!name) return null;
      const raw = localStorage.getItem("locationsList");
      if (!raw) return null;
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return null;
      const found = list.find(
        (loc) => String(loc?.name || "").toLowerCase() === name.toLowerCase()
      );
      return found?.id ? Number(found.id) : null;
    } catch {
      return null;
    }
  };

  return useMemo(() => {
    const activeLocationId = getActiveLocationId();

    return (p) => {
      const productLocations = Array.isArray(p?.locations) ? p.locations : [];

      const matchedLoc = locationFilter
        ? productLocations.find(
            (loc) =>
              String(loc?.name || "").toLowerCase() ===
              locationFilter.toLowerCase()
          )
        : productLocations[0] ?? null;

      const belongsToLocation = locationFilter
        ? matchedLoc != null
        : true;

      const currencySymbol = matchedLoc?.currency?.symbol || "$";

      const fmt = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const n = Number(value);
        if (Number.isNaN(n)) return null;
        return `${currencySymbol}${n.toFixed(2)}`;
      };

      const regular = Number(
        matchedLoc?.price_regular ?? p?.base_regular_price ?? 0
      );

      const baseCurrent = Number(
        matchedLoc?.price_current ?? p?.base_current_price ?? 0
      );

      const flashPrice =
        matchedLoc?.flash_sales_price !== null &&
        matchedLoc?.flash_sales_price !== undefined &&
        matchedLoc?.flash_sales_price !== ""
          ? Number(matchedLoc.flash_sales_price)
          : null;

      const isOnFlashSale =
        flashPrice !== null &&
        !Number.isNaN(flashPrice) &&
        flashPrice > 0 &&
        flashPrice < baseCurrent;

      const currentNum = isOnFlashSale ? flashPrice : baseCurrent;
      const oldNum = isOnFlashSale ? baseCurrent : regular;

      const discountPercent = (() => {
        const r = isOnFlashSale ? baseCurrent : regular;
        const c = currentNum;
        if (!r || r <= 0 || c >= r) return null;
        const pct = Math.round(((r - c) / r) * 100);
        return pct > 0 ? pct : null;
      })();

      // ✅ IMPROVED: Find location_product_price_id with better fallbacks
      let locationProductPriceId = null;

      // Strategy 1: From matched location's pivot.id
      if (matchedLoc?.pivot?.id) {
        locationProductPriceId = Number(matchedLoc.pivot.id);
      }
      
      // Strategy 2: From matched location's location_product_price_id field
      if (!locationProductPriceId && matchedLoc?.location_product_price_id) {
        locationProductPriceId = Number(matchedLoc.location_product_price_id);
      }

      // Strategy 3: From location_prices array using activeLocationId
      if (!locationProductPriceId && activeLocationId) {
        const locationPrices = p?.location_prices || p?.locationPrices || [];
        const priceRecord = locationPrices.find(
          (lp) => Number(lp?.location_id) === Number(activeLocationId)
        );
        if (priceRecord?.id) {
          locationProductPriceId = Number(priceRecord.id);
        }
      }

      // Strategy 4: Search all location_prices for matching location name
      if (!locationProductPriceId && locationFilter) {
        const locationPrices = p?.location_prices || p?.locationPrices || [];
        const priceRecord = locationPrices.find(
          (lp) => String(lp?.location?.name || "").toLowerCase() === locationFilter.toLowerCase()
        );
        if (priceRecord?.id) {
          locationProductPriceId = Number(priceRecord.id);
        }
      }

      // Strategy 5: Direct from product using location ID
      if (!locationProductPriceId && activeLocationId && p?.id) {
        // Check if there's a direct field like product_location_id
        if (p?.product_location_id) {
          locationProductPriceId = Number(p.product_location_id);
        } else if (p?.pivot?.id) {
          locationProductPriceId = Number(p.pivot.id);
        }
      }


      return {
        belongsToLocation,
        newPrice: fmt(currentNum),
        oldPrice: fmt(oldNum),
        currencySymbol,
        currentNum,
        oldNum,
        discountPercent,
        isOnFlashSale,
        discount: isOnFlashSale
          ? `🔥 ${discountPercent ? `${discountPercent}% OFF` : "SALE"}`
          : discountPercent
          ? `${discountPercent}% OFF`
          : null,
        locationProductPriceId, // ✅ Return this for cart operations
      };
    };
  }, [locationFilter]);
};